import Big from 'big.js'
import type {Store} from 'jotai'
import {groupBy} from 'remeda'

import {isAuthenticatedAtom} from '@/auth/hooks/useIsAuthenticated'
import getAccountInformation from '@/auth/utils/getAccountInformation'
import type {CartItem} from '@/models/cartModel'

import {type Contact, getDefaultContact} from './components/model'
import {getWalletBalance, setWalletRoundUp} from './paymentService'

const customItemsStorage = createJSONStorage<CartItem[]>(() => sessionStorage)
const customItemsAtom = atomWithStorage<CartItem[]>('customItems', [], customItemsStorage)

export function useCustomItems() {
  return useAtom(customItemsAtom)
}

export function useSetCustomItems() {
  return useSetAtom(customItemsAtom)
}

export function useCustomCartValue() {
  const [customItems] = useCustomItems()

  // Follow the same schema of useCart
  return {
    items: groupBy(customItems, item => item.seller ?? item.retailer),
    itemsLength: customItems.reduce((acc, item) => acc + item.quantity, 0),
    itemsValue: customItems.reduce(
      (acc, item) => acc.plus(Big(item.price).times(item.quantity)),
      Big(0),
    ),
    originalItems: customItems,
  }
}

const contactInfoStorage = createJSONStorage<Contact>(() => sessionStorage)
export const contactAtom = atomWithStorage<Contact>(
  'contact',
  getDefaultContact(),
  contactInfoStorage,
  {
    getOnInit: true,
  },
)

export function useContact() {
  return useAtom(contactAtom)
}

export function useSetContact() {
  return useSetAtom(contactAtom)
}

const roundUpAtom = (() => {
  const INITIAL_VALUE = false
  const KEY = 'roundUp'

  const baseAtom = atom(INITIAL_VALUE)

  baseAtom.onMount = setValue => {
    void (async () => {
      const localValue = localStorage.getItem(KEY)
      if (localValue !== null) {
        const parsed = JSON.parse(localValue)
        if (typeof parsed === 'boolean') {
          setValue(parsed)
        }
      }

      const isAuthenticated = !!getAccountInformation()
      if (isAuthenticated) {
        const data = await getWalletBalance({})
        setValue(data.moreInfo.roundUp)
      }
    })()
  }

  const derivedAtom = atom(
    get => get(baseAtom),
    (get, set, update: boolean | ((prev: boolean) => boolean)) => {
      const nextValue = typeof update === 'function' ? update(get(baseAtom)) : update
      set(baseAtom, nextValue)

      localStorage.setItem(KEY, JSON.stringify(nextValue))

      const isAuthenticated = get(isAuthenticatedAtom)
      if (isAuthenticated) {
        void setWalletRoundUp({roundUp: nextValue}).catch(error => {
          console.error('Failed to sync roundUp to remote:', error)
        })
      }
    },
  )

  return derivedAtom
})()

export function useRoundUp() {
  return useAtom(roundUpAtom)
}

export function useSetRoundUp() {
  return useSetAtom(roundUpAtom)
}

export function getRoundUp(store: Store) {
  return store.get(roundUpAtom)
}

export function useGetRoundUp() {
  const store = useStore()
  return getRoundUp(store)
}
